import { useRef, useState, useEffect } from "react";
import { validateSlug } from "../../../utils/api/slugValidation";

// Custom hook for debouncing values
export const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Custom hook for managing notifications
export const useNotification = () => {
  const [notification, setNotification] = useState<{
    title: string;
    message: string;
    color: string;
  } | null>(null);

  const showNotification = (title: string, message: string, color: string) => {
    setNotification({ title, message, color });
  };

  const clearNotification = () => {
    setNotification(null);
  };

  return { notification, showNotification, clearNotification };
};

// Custom hook for slug validation
export const useSlugValidation = (vanitySlug: string | undefined) => {
  const [vanitySlugError, setVanitySlugError] = useState<string | null>(null);
  const [slugAvailability, setSlugAvailability] = useState<{
    isAvailable: boolean;
    message: string;
  } | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const debouncedVanitySlug = useDebounce(vanitySlug || "", 500);

  const validateVanitySlug = (slug: string): boolean => {
    if (slug.length > 15) {
      setVanitySlugError("Vanity URL cannot be longer than 15 characters");
      return false;
    }

    if (!/^[a-zA-Z0-9-]*$/.test(slug)) {
      setVanitySlugError("Only letters, numbers, and hyphens are allowed");
      return false;
    }

    setVanitySlugError(null);
    return true;
  };

  useEffect(() => {
    const checkSlugAvailability = async () => {
      if (!debouncedVanitySlug) {
        setSlugAvailability(null);
        return;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      try {
        const storedBusinessInfo = JSON.parse(
          localStorage.getItem("businessInfo") as string
        );
        if (storedBusinessInfo?.vanitySlug === debouncedVanitySlug) {
          setSlugAvailability(null);
          return;
        }

        const isInUse = await validateSlug(
          debouncedVanitySlug,
          abortControllerRef.current.signal
        );
        setSlugAvailability({
          isAvailable: !isInUse,
          message: isInUse ? "Url is already in use" : "Url is available",
        });
      } catch (error: unknown) {
        if (error instanceof Error && error.name !== "AbortError") {
          setSlugAvailability({
            isAvailable: false,
            message: "Error checking URL availability",
          });
        }
      }
    };

    checkSlugAvailability();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [debouncedVanitySlug]);

  return {
    vanitySlugError,
    slugAvailability,
    validateVanitySlug,
    setSlugAvailability,
  };
};

// Custom hook for color validation
export const useColorValidation = () => {
  const [isColorValid, setIsColorValid] = useState(true);

  const isColorTooLight = (hexColor: string): boolean => {
    const hex = hexColor.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.7;
  };

  const validateColor = (color: string) => {
    const isValid = !isColorTooLight(color);
    setIsColorValid(isValid);
    return isValid;
  };

  return { isColorValid, validateColor };
};

// Custom hook for link validation
export const useLinkValidation = () => {
  const [calendlyLinkError, setCalendlyLinkError] = useState(false);
  const [paymentLinkError, setPaymentLinkError] = useState(false);

  const isValidLink = (link: string) => {
    return link.startsWith("https://");
  };

  const validateCalendlyLink = (link: string) => {
    const isValid = isValidLink(link) || link.length < 10;
    setCalendlyLinkError(!isValid);
    return isValid;
  };

  const validatePaymentLink = (link: string) => {
    const isValid = isValidLink(link) || link.length < 10;
    setPaymentLinkError(!isValid);
    return isValid;
  };

  return {
    calendlyLinkError,
    paymentLinkError,
    validateCalendlyLink,
    validatePaymentLink,
  };
};
